import { useMutation } from '@tanstack/react-query'
import type { UploadFile } from 'antd'
import type { RcFile } from 'antd/es/upload'
import { useState } from 'react'

import { clearJourney, msSince, readJourney, track } from 'src/analytics'
import {
  onboardingProfileCreateMutation,
  onboardingProfilePartialUpdateMutation,
  organizationsAccessAddCreateMutation,
  organizationsOrganizationsAuthMethodsPartialUpdateMutation,
  organizationsOrganizationsCreateMutation,
  organizationsOrganizationsPartialUpdateMutation,
  type IndustryEnum,
  type OnboardingProfileResponse,
  type OrganizationCreate,
  type OrganizationCreateWritable
} from 'src/api/main'
import { buildAuthMethodsPayload } from 'src/features/organizations/components/OrganizationSettings/authMethodsPayload'
import { useUpdateUserContext } from 'src/features/organizations/hooks'
import { buildWorkspaceInviteUrl } from 'src/features/workspaces/utils/inviteUrl'
import { usePresignedUpload } from 'src/hooks'
import { useShowApiErrors } from 'src/utils'
import { uploadFiles } from 'src/utils/uploadFiles'

import { DEFAULT_AUTH_METHODS } from './constants'
import type { DraftInvite, OnboardingDraft } from './types'
import { markChecklistIntro, markWorkspaceWelcome } from './welcomeHandoff'

/** Outcome of the create chain, rendered by the Done screen. */
export type CreateWorkspaceOutcome = {
  organization: OrganizationCreate
  /**
   * Setup-job id to poll for provisioning progress, when one started. Comes
   * from the onboarding profile: saving the profile is what seeds the workspace
   * from its industry, so the job is that call's answer rather than something
   * this app asks for separately.
   */
  setupJobId?: number
  authApplied: boolean
  invitesSent: number
  invitesFailed: boolean
  /** The profile could not be saved, so nothing was seeded from the industry. */
  profileFailed: boolean
  /** The workspace exists but its picture could not be attached. */
  photoFailed: boolean
}

type RunInput = {
  draft: OnboardingDraft
  invites: DraftInvite[]
  photoFiles: UploadFile[]
}

/**
 * Onboarding no longer asks which sign-in methods to enable, so every workspace
 * starts on {@link DEFAULT_AUTH_METHODS}. OIDC providers stay off: they need
 * client credentials, and the only place that can collect them is the workspace
 * Authentication page.
 */
const authMethodValues = (): Record<string, unknown> => ({
  ...DEFAULT_AUTH_METHODS
})

/**
 * Turns the wizard draft into a workspace.
 *
 * Order matters: the organization must exist before the onboarding profile,
 * auth methods or invites can reference it. The profile comes next and is the
 * step that seeds the workspace — the platform reads `industry` and starts the
 * provisioning job, which is why this app no longer asks for an environment
 * build of its own. Everything after that runs with `allSettled`, so one
 * non-critical failure (an invite bouncing, say) still lands the user on the
 * Done screen with an accurate summary.
 *
 * Nothing here reports itself to analytics beyond `onboarding_completed`: the
 * platform emits `organization_created`, `onboarding_profile_saved`,
 * `provisioning_started`, `invitations_sent`, `auth_method_enabled` and
 * `activation_reached` from the very calls below, and a second copy from this
 * side would double-count every one of them.
 *
 * Intentionally separate from `WorkspacesNewPage`'s own submit handler: that
 * page is the flag-OFF path and stays untouched.
 */
export const useCreateWorkspace = () => {
  const showApiErrors = useShowApiErrors()
  const { uploadFile, submitWebhook } = usePresignedUpload()
  const { updateUser } = useUpdateUserContext()
  const [isRunning, setIsRunning] = useState(false)

  const { mutateAsync: createOrganization } = useMutation(
    organizationsOrganizationsCreateMutation()
  )
  const { mutateAsync: saveProfile } = useMutation(
    onboardingProfileCreateMutation()
  )
  const { mutateAsync: patchProfile } = useMutation(
    onboardingProfilePartialUpdateMutation()
  )
  const { mutateAsync: updateAuthMethods } = useMutation(
    organizationsOrganizationsAuthMethodsPartialUpdateMutation()
  )
  const { mutateAsync: sendInvites } = useMutation(
    organizationsAccessAddCreateMutation()
  )
  const { mutateAsync: patchOrganization } = useMutation(
    organizationsOrganizationsPartialUpdateMutation()
  )

  /**
   * Signed URL, S3 PUT, the webhook that marks the attachment uploaded, then the
   * PATCH that hangs it on the workspace.
   *
   * Runs after the workspace exists, not when the file was picked: the
   * attachment endpoints scope themselves to the caller's workspace and take no
   * organization of their own, so a first-ever workspace had nothing for them to
   * attach to — they answered "a workspace is required for this upload" and the
   * whole creation stopped on a picture.
   */
  const attachPhoto = async (organizationId: number, file: UploadFile) => {
    const signed = await uploadFile(
      file.originFileObj as RcFile,
      organizationId,
      { isPublic: true }
    )
    await uploadFiles([{ ...file, response: signed }], { submitWebhook })
    await patchOrganization({
      path: { id: organizationId },
      body: { photo: signed.id }
    })
  }

  const run = async ({
    draft,
    invites,
    photoFiles
  }: RunInput): Promise<CreateWorkspaceOutcome | null> => {
    // The two answers the platform insists on, taken before anything is
    // created. They are collected on the first two steps and cannot be missing
    // by the time this runs, but this is the seam where a screen's "not
    // answered yet" has to become a request body — so it is the one place worth
    // saying so, rather than inventing an industry or a headcount further down.
    const { companySize, industry } = draft
    if (!companySize || !industry) return null

    setIsRunning(true)
    try {
      const pickedFile = photoFiles[0]

      const body: OrganizationCreateWritable = {
        name: draft.name.trim(),
        domain_name: draft.handle,
        photo: null,
        // Mirrors the existing wizard, which marks every workspace it creates as
        // a demo one. Not a decision made here — changing it needs the backend's
        // `is_demo` semantics confirmed first.
        is_demo: true
      }

      let organization: OrganizationCreate
      try {
        organization = await createOrganization({ body })
      } catch (error) {
        showApiErrors(error)

        return null
      }

      const organizationId = Number(organization.id)

      // Explicitly scoped rather than left to the endpoint's fallback: that
      // falls back to the token's organization, which for anyone who already
      // had a workspace is the wrong one — the profile would be attached to
      // where they came from instead of what they just created.
      const scope = { organization: organizationId }

      let profile: OnboardingProfileResponse | undefined
      try {
        profile = await saveProfile({
          query: scope,
          body: {
            // The value is a demo-preset codename, and the presets endpoint types
            // those as a plain string while the profile now accepts only the seven
            // `IndustryEnum` members. They coincide today; the cast is where that
            // assumption lives, rather than a narrower draft type claiming a
            // guarantee the preset list does not give.
            industry: industry as IndustryEnum,
            company_size: companySize,
            primary_job: draft.priority ?? undefined
          }
        })
      } catch (error) {
        showApiErrors(error)
      }

      /**
       * One request for every address, with no role attached.
       *
       * The endpoint used to take a `role` per batch, which is why these were
       * grouped by it. It now rejects the field outright —
       * `role: ["Assign workspace roles through the membership roles endpoint."]`
       * — so nothing was being invited at all. Roles moved to
       * `POST /organizations/access/{id}/roles/`, which takes numeric
       * `OrganizationRole` ids rather than the old string enum; wiring that up
       * needs the role catalogue in the UI and is a migration of its own, so for
       * now the invitations go out and the roles do not.
       */
      const inviteEmails = invites.map(({ email }) => email)

      const inviteUrl = buildWorkspaceInviteUrl(organization.domain_name)

      const [, authResult, ...inviteResults] = await Promise.allSettled([
        updateUser({}),
        updateAuthMethods({
          path: { id: organizationId },
          body: {
            auth_methods: buildAuthMethodsPayload(authMethodValues())
          }
        }),
        ...(inviteEmails.length > 0
          ? [
              sendInvites({
                body: {
                  organization: organizationId,
                  emails: inviteEmails,
                  invite_url: inviteUrl
                }
              })
            ]
          : [])
      ])

      /**
       * After the session refresh above, never beside it.
       *
       * The attachment endpoints resolve the workspace from the caller's
       * session, so a presign racing `updateUser` still answered "a workspace is
       * required for this upload" — the very error moving the upload after
       * creation was meant to fix. Sequencing it behind the refresh is the whole
       * point; running it inside the same `allSettled` threw that away.
       */
      let photoFailed = false

      if (pickedFile?.originFileObj) {
        try {
          await attachPhoto(organizationId, pickedFile)

          // The session was refreshed above, before the picture existed, so its
          // copy of the workspace still has `photo: null` — and the switcher and
          // the overview hero read the logo from exactly there. Without this the
          // workspace wears its initial everywhere while its own settings page
          // shows the picture.
          await updateUser({})
        } catch {
          // Reported by the summary's own warning, not by a toast: the raw
          // message describes a backend constraint, not anything the owner did,
          // and the workspace itself was created fine.
          photoFailed = true
        }
      }

      // Attribution is a PATCH of its own, not part of the profile above: the
      // screen that asks it is on its way out of the wizard, and the platform
      // treats the industry half as write-once — so the two answers must not
      // share a request that a later flow would have to send twice.
      if (draft.attribution && profile) {
        try {
          await patchProfile({
            query: scope,
            body: { source: draft.attribution }
          })
        } catch {
          /* Attribution is optional and reportable later; nothing depends on it. */
        }
      }

      // Both claimed by the shell the first time the workspace is opened: the
      // welcome plays, and the setup checklist opens itself behind it.
      markWorkspaceWelcome(organizationId)
      markChecklistIntro(organizationId)

      if (authResult.status === 'rejected') showApiErrors(authResult.reason)

      const rejectedInvite = inviteResults.find(
        (result) => result.status === 'rejected'
      )

      const invitesSent =
        inviteResults.filter((result) => result.status === 'fulfilled').length >
        0
          ? invites.length
          : 0

      const journey = readJourney()

      track('onboarding_completed', {
        invites_sent: invitesSent,
        industry,
        duration_ms: journey ? (msSince(journey.startedAt) ?? 0) : 0
      })

      // The journey is over. Left registered, `journey_id` would ride along on
      // every event from ordinary product use afterwards.
      clearJourney()

      return {
        organization,
        setupJobId: profile?.provisioning.job_id ?? undefined,
        authApplied: authResult.status === 'fulfilled',
        invitesSent,
        invitesFailed: rejectedInvite !== undefined,
        profileFailed: profile === undefined,
        photoFailed
      }
    } finally {
      setIsRunning(false)
    }
  }

  return { run, isRunning }
}
